import { Price } from '../../../../../../../components/ToolkitV2/CardV2/components/Price/Price';
import { Image } from '../../../../../../../components/ToolkitV2/CardV2/components/Image/Image';
import { TrustIndicatorTags } from '../../../../../../../components/PageWrappers/SharedComponents/TrustIndicatorTags/TrustIndicatorTags';
import { Card } from '../../../../../../../components/ToolkitV2/CardV2/Card';
import { BasicHeader } from '../../../../../../../components/ToolkitV2/CardV2/components/BasicHeader/BasicHeader';
import { MediaCount } from '../../../../../../../components/Toolkit/Tags/MediaCount';
import { Spacer } from '../../../../../../../helpers/genericStyles';

import { useSaveAd } from '../../../../../../../components/Support/UseSaveAd/useSaveAd';

import { WithImageOverlay } from '../../../../../../../components/ToolkitV2/CardV2/enhancements/WithImageOverlay/WithImageOverlay';
import { WithHeader } from '../../../../../../../components/ToolkitV2/CardV2/enhancements/WithHeader/WithHeader';

import { Desktop } from '../../../../../../../components/Support/Desktop/Desktop';

import { InfoTitle } from '../../../../../../../components/Toolkit/InfoTitle/InfoTitle';

import { SaveAd } from '../../../../../../../components/Toolkit/SaveAd/SaveAd';
import type { RelatedAdsCardProps } from '../../../../../../../components/PageWrappers/AdDetails/components/RelatedAds/components/RelatedAdsCard/RelatedAdsCard.typed';
import { useRelatedAdsCard } from '../../../../../../../components/PageWrappers/AdDetails/components/RelatedAds/components/RelatedAdsCard/RelatedAdsCard.hook';

const RelatedAdsCard = (props: RelatedAdsCardProps) => {
  const {
    item: {
      dealer,
      section,
      id,
      userSaved = false,
      age = '',
      county = '',
      countyTown = '',
      wanted,
      deliveryAvailable = false,
      cardPayments,
      meritsV2,
      specialOffer,
      greenlightVerified,
      manufacturerApprovedBadge,
      photos,
      imageAlt,
      mediaCount,
      header,
      currency,
      price,
      financeSummary,
      keyInfo,
      oldPriceView,
      priceOnRequest,
    },
    isGrid,
  } = props;

  const {
    dealerHeader,
    orientationConfig,
    includesPhoto,
    imageSrc,
    imageSrcWebp,
    formattedMetaData,
  } = useRelatedAdsCard({
    dealer,
    isGrid,
    mediaCount,
    photos,
    keyInfo,
    age,
    countyTown,
    county,
  });

  const { onSave, isAdSaved } = useSaveAd({
    adId: id,
    userSaved: userSaved,
    section: section?.name,
    sellerType: dealer ? 'DEALER' : 'PRIVATE',
    sectionName: section?.displayName,
  });

  const { title, subTitle, logo } = dealerHeader;

  const showBorder = Boolean(title && logo && !isGrid);

  return (
    <WithHeader
      isGrid={isGrid}
      elements={
        title &&
        logo &&
        !isGrid && (
          <>
            <InfoTitle
              title={title}
              listItems={[subTitle]}
              illustration={logo}
              variant="SMALL"
              shouldTruncate
            />
          </>
        )
      }
      showBorder={showBorder}
    >
      <Card
        {...orientationConfig}
        image={
          <WithImageOverlay
            bottomLeftSlot={
              includesPhoto && <MediaCount mediaCount={mediaCount} />
            }
            bottomRightSlot={
              isGrid && (
                <SaveAd
                  toggleSave={onSave}
                  isAdSaved={isAdSaved}
                  hideText
                  variant="OVERLAY"
                />
              )
            }
          >
            <Image
              src={imageSrc}
              imgSrcWebp={imageSrcWebp}
              alt={imageAlt}
              heightVertical={225}
              widthHorizontalMedium={320}
              loading="lazy"
              {...orientationConfig}
            />
          </WithImageOverlay>
        }
        header={
          <div>
            {!isGrid && (
              <Desktop>
                <Spacer marginBottom="S8" />
              </Desktop>
            )}
            <BasicHeader
              title={header}
              titleSmallFontSize={isGrid ? 'B12' : undefined}
              keyInfoItemSmallFontSize={isGrid ? 'B12' : undefined}
              metaData={formattedMetaData}
            />
          </div>
        }
        body={
          <TrustIndicatorTags
            deliveryAvailable={deliveryAvailable}
            cardPayments={cardPayments}
            meritsV2={meritsV2}
            specialOffer={specialOffer}
            wanted={wanted}
            greenlightVerified={greenlightVerified}
            manufacturerApprovedBadge={manufacturerApprovedBadge}
          />
        }
        gapSmall={isGrid ? 'M16' : 'S8'}
        gapMedium={isGrid ? 'S8' : undefined}
        paddingSmall={isGrid ? 'S4' : 'S8'}
        paddingMedium={isGrid ? 'S8' : 'M16'}
        footer={
          <>
            <Price
              currency={currency}
              price={price}
              pricePerMonth={financeSummary?.price}
              priceOnRequest={priceOnRequest}
              isGrid={isGrid}
              oldPrice={
                oldPriceView?.showOldPrice ? oldPriceView.oldPrice : null
              }
              priceSmallFontSize={isGrid ? 'B12' : undefined}
            />
            {!isGrid && (
              <SaveAd toggleSave={onSave} isAdSaved={isAdSaved} hideText />
            )}
          </>
        }
      />
    </WithHeader>
  );
};

export { RelatedAdsCard };
